import { groundEquivalentPrintZoom } from './mapBorders'
import { lineStrokeWidthCssPx } from './mapLines'
import { PAGE_PX_PER_MM } from './printLayout'
import type { PrintProjector } from './printProjection'
import type { GeoPt } from './types'

/** Screen and paper evaluate the same width law at the same ground scale. */
export function lineStrokeMm(
  width: number | undefined,
  scale: number,
  lat: number,
  thickness = 1,
): number {
  return (
    lineStrokeWidthCssPx(
      width,
      groundEquivalentPrintZoom({ scale, lat }),
      thickness,
    ) / PAGE_PX_PER_MM
  )
}

/** The screen's centre-anchored triangle, constructed in the sheet's plane. */
export function printArrowHead(
  points: readonly GeoPt[],
  widthMm: number,
  scale: number,
  lat: number,
  sheet: PrintProjector,
): GeoPt[] | null {
  const end = points.at(-1)
  if (!end) return null
  const previous = points
    .slice(0, -1)
    .reverse()
    .find((p) => p.lat !== end.lat || p.lon !== end.lon)
  if (!previous) return null
  const [x, y] = sheet.forward(end)
  const [fx, fy] = sheet.forward(previous)
  const length = Math.hypot(x - fx, y - fy)
  if (!length) return null
  const ux = (x - fx) / length,
    uy = (y - fy) / length
  const mmToCrs = (scale / 1000) * sheet.crsMetresPerGroundMetre(lat)
  const h = Math.min(5 * widthMm, 56 / PAGE_PX_PER_MM) * mmToCrs
  const halfWidth = h * Math.tan(Math.PI / 8)
  return [
    sheet.inverse([x + (ux * h) / 2, y + (uy * h) / 2]),
    sheet.inverse([
      x - (ux * h) / 2 - uy * halfWidth,
      y - (uy * h) / 2 + ux * halfWidth,
    ]),
    sheet.inverse([
      x - (ux * h) / 2 + uy * halfWidth,
      y - (uy * h) / 2 - ux * halfWidth,
    ]),
  ]
}
